// functions/Fx_ScheduleJobDates.ts

import type { RepeatPattern } from "./Fx_ScheduleJobTypes";

const pad2 = (n: number) => String(n).padStart(2, "0");

// Convert a Date -> "YYYY-MM-DDTHH:mm" (local)
export const toLocalInputDateTime = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

// Adds days to a "YYYY-MM-DDTHH:mm" local string and returns same format
export const addDaysLocal = (isoLocal: string, days: number) => {
  const d = new Date(isoLocal); // treated as local time by JS
  d.setDate(d.getDate() + days);
  return toLocalInputDateTime(d);
};

// Parse a date-like value into milliseconds using a local-time interpretation
// for strings such as "YYYY-MM-DDTHH:mm".
export const parseDateLikeToMs = (value: string | number | Date | null | undefined) => {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const isoLike = trimmed.replace(/\s+/g, " ");

    // Support inputs that are already ISO-like or date-only strings.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?$/.test(isoLike)) {
      const [datePart, timePart] = isoLike.split("T");
      if (timePart && !timePart.includes(":")) {
        return null;
      }

      const localDate = new Date(`${datePart}T${timePart || "00:00"}`);
      const ms = localDate.getTime();
      return Number.isFinite(ms) ? ms : null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(isoLike)) {
      const localDate = new Date(`${isoLike}T00:00`);
      const ms = localDate.getTime();
      return Number.isFinite(ms) ? ms : null;
    }

    const ms = new Date(isoLike).getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  return null;
};

// Past-check helper (used for confirm dialogs)
export const isPastByMs = (isoLike: string) => {
  const ms = parseDateLikeToMs(isoLike);
  return ms !== null && ms < Date.now();
};

// Your existing timezone adjust helper (kept identical)
export const addHoursIso = (dateString: string, hoursToAdj: number) => {
  const originalDate = new Date(dateString);
  originalDate.setHours(originalDate.getHours() - hoursToAdj);
  return originalDate.toISOString();
};

// Build occurrence start/end (keeps your duration logic)
export const buildOccurrenceTimes = (args: {
  startIsoLocal: string;
  endIsoLocal: string;
  i: number;
  repeatPattern: RepeatPattern;
}) => {
  const { startIsoLocal, endIsoLocal, i, repeatPattern } = args;

  const start0 = new Date(startIsoLocal);
  const end0 = new Date(endIsoLocal);
  const durationMs = end0.getTime() - start0.getTime();

  const stepDays = repeatPattern === "weekly" ? 7 : 1;

  const startI = new Date(startIsoLocal);
  startI.setDate(startI.getDate() + stepDays * i);

  const endI = new Date(startI.getTime() + durationMs);

  return {
    start: toLocalInputDateTime(startI),
    end: toLocalInputDateTime(endI),
  };
};
